/**
 * GuestScheduleCalendar — month-grid overview of a guest's activities & services.
 * A read surface: it lays the same schedule items onto calendar days so the
 * operator sees density and clashes at a glance. Per-item actions stay in the
 * list view. Starts on the month of the earliest item and pages month by month.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from '@/core';
import { Button } from '@/theme/components/ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { GuestScheduleItem } from '../types';

const WEEKDAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;

function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

interface Props {
  items: GuestScheduleItem[];
}

export function GuestScheduleCalendar({ items }: Props) {
  const { t } = useTranslation();

  const byDay = useMemo(() => {
    const map = new Map<string, GuestScheduleItem[]>();
    for (const it of items) {
      if (!it.date) continue;
      (map.get(it.date) ?? map.set(it.date, []).get(it.date)!).push(it);
    }
    return map;
  }, [items]);

  const earliest = useMemo(() => {
    const dates = items.map((i) => i.date).filter(Boolean).sort() as string[];
    return dates[0] ?? ymd(new Date());
  }, [items]);

  const [cursor, setCursor] = useState(() => {
    const [y, m] = earliest.split('-').map(Number);
    return { year: y, month: m - 1 }; // month 0-indexed
  });

  const grid = useMemo(() => {
    const first = new Date(cursor.year, cursor.month, 1);
    const lead = (first.getDay() + 6) % 7; // Monday-first offset
    const daysInMonth = new Date(cursor.year, cursor.month + 1, 0).getDate();
    const cells: (string | null)[] = [];
    for (let i = 0; i < lead; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(ymd(new Date(cursor.year, cursor.month, d)));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [cursor]);

  const monthLabel = new Intl.DateTimeFormat(undefined, { month: 'long', year: 'numeric' })
    .format(new Date(cursor.year, cursor.month, 1));
  const todayStr = ymd(new Date());

  const shift = (delta: number) => setCursor((c) => {
    const m = c.month + delta;
    return { year: c.year + Math.floor(m / 12), month: ((m % 12) + 12) % 12 };
  });

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium capitalize">{monthLabel}</p>
        <div className="flex gap-1">
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => shift(-1)} aria-label={t('common.previous', 'Previous')}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => shift(1)} aria-label={t('common.next', 'Next')}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-semibold uppercase tracking-[0.04em] text-muted-foreground">
        {WEEKDAY_KEYS.map((k) => (
          <div key={k}>{t(`services.runs.weekdays.${k}`, k)}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {grid.map((day, idx) => {
          if (!day) return <div key={`b-${idx}`} className="min-h-16 rounded-md" />;
          const dayItems = byDay.get(day) ?? [];
          const isToday = day === todayStr;
          return (
            <div
              key={day}
              className={`min-h-16 rounded-md border p-1 text-left ${
                isToday ? 'border-ocean-500 bg-ocean-700/5' : 'border-border'
              }`}
            >
              <p className={`mb-0.5 text-[11px] tabular-nums ${isToday ? 'font-semibold text-ocean-700' : 'text-muted-foreground'}`}>
                {Number(day.slice(-2))}
              </p>
              <div className="space-y-0.5">
                {dayItems.slice(0, 2).map((it) => (
                  <div
                    key={`${it.kind}-${it.id}`}
                    title={it.title}
                    className={`truncate rounded px-1 py-0.5 text-[10px] leading-tight ${
                      it.status === 'cancelled' ? 'line-through opacity-50' : ''
                    } ${it.kind === 'activity' ? 'bg-ocean-700/10 text-ocean-800' : 'bg-coral-500/10 text-coral-800'}`}
                  >
                    {it.startTime ? `${it.startTime.slice(0, 5)} ` : ''}{it.title}
                  </div>
                ))}
                {dayItems.length > 2 && (
                  <p className="px-1 text-[10px] text-muted-foreground">+{dayItems.length - 2}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
